import { UiState } from 'instantsearch.js';
import singletonRouter from 'next/router';
import { createInstantSearchRouterNext } from 'react-instantsearch-router-nextjs';
import { algoliaSortOptions } from './sorting';
import { InstantSearchRouting, RouteState } from './types';
import { ALGOLIA_INDEX_ID_PRODUCT_LIST } from '~/services/instantsearch/variables';

const removeEmptyProperties = (record: Record<string, unknown>) =>
    Object.entries(record).reduce((newRecord, entry) => {
        const [key, value] = entry;

        // We'll remove these falsy values: undefined, null, and ""
        if (value === null || value === undefined || value === '') return newRecord;

        return {
            ...newRecord,
            [key]: value,
        };
    }, {});

const getSearchTermFromUrl = (url: string): string | null =>
    new URL(url, window.location.origin).searchParams.get('query');

const hasSearchTermChanged = (nextUrl: string): boolean =>
    getSearchTermFromUrl(singletonRouter.asPath) !== getSearchTermFromUrl(nextUrl);

export const createInstantSearchRouting = (serverUrl?: string): InstantSearchRouting => ({
    router: createInstantSearchRouterNext({
        singletonRouter,
        serverUrl,
        beforePopState: ({ state, libraryBeforePopState }) =>
            libraryBeforePopState(state) || hasSearchTermChanged(state.as),
        routerOptions: {
            cleanUrlOnDispose: true,
            createURL({ qsModule, location, routeState }) {
                const url = new URL(location.toString());
                const currentParams = qsModule.parse(url.search, { ignoreQueryPrefix: true });

                const instantSearchKeys = [
                    'refinementList',
                    'page',
                    'sort',
                    'range',
                    'toggle',
                    'query',
                ];

                const otherKeys = Object.keys(currentParams).filter(
                    (key) => !instantSearchKeys.includes(key),
                );

                /**
                 * We will make sure that non-InstantSearch search parameters are persisted by including them
                 * on the URL that we create with our route state. We will merge them before stringifying.
                 */
                if (otherKeys.length) {
                    const otherParams = otherKeys.reduce(
                        (params, key) => {
                            params[key] = currentParams[key];

                            return params;
                        },
                        {} as Record<string, unknown>,
                    );

                    url.search = qsModule.stringify({
                        ...otherParams,
                        ...routeState,
                    });
                } else {
                    url.search = qsModule.stringify(routeState);
                }

                return url.toString();
            },
        },
    }),

    stateMapping: {
        stateToRoute(uiState: UiState): RouteState {
            const productListState = uiState[ALGOLIA_INDEX_ID_PRODUCT_LIST] || {};

            /**
             * TODO (IMC-2274): Use sort options from Frame data.
             */
            const sort = algoliaSortOptions.find(
                (option) => option.value === productListState.sortBy,
            )?.label;

            return removeEmptyProperties({
                refinementList: productListState.refinementList,
                page: productListState.page,
                range: productListState.range,
                sort,
                toggle: productListState.toggle,
                query: productListState.query,
            });
        },

        routeToState(routeState: RouteState): UiState {
            /**
             * TODO (IMC-2274): Use sort options from Frame data.
             */
            const sortBy = algoliaSortOptions.find(
                (option) => option.label === routeState.sort,
            )?.value;

            return {
                [ALGOLIA_INDEX_ID_PRODUCT_LIST]: removeEmptyProperties({
                    query: routeState.query,
                    refinementList: routeState.refinementList,
                    page: routeState.page,
                    toggle: routeState.toggle,
                    range: routeState.range,
                    sortBy,
                }),
            };
        },
    },
});

class ClientSideRoutingSingleton {
    private static _instance: ClientSideRoutingSingleton;

    routing: InstantSearchRouting;

    private constructor() {
        this.routing = createInstantSearchRouting();

        this.refreshRouting = this.refreshRouting.bind(this);
    }

    static getInstance() {
        if (!ClientSideRoutingSingleton._instance) {
            ClientSideRoutingSingleton._instance = new ClientSideRoutingSingleton();
        }

        return ClientSideRoutingSingleton._instance;
    }

    refreshRouting() {
        this.routing = createInstantSearchRouting();
    }
}

/**
 * We will use a singleton for client-side routing to make it resilient against hydration errors.
 *
 * TODO(IMC-811): Remove once hydration errors are resolved.
 */
export const clientSideRoutingSingleton = ClientSideRoutingSingleton.getInstance();
